import React from 'react';

import { TIMELINE_TYPE_FILTER } from '../../constants/patientCard';
import { COUNT_UNKNOWN } from '../../utils/patientCardLanes';

const CHIP_DEFS = [
  { key: TIMELINE_TYPE_FILTER.ALL, label: 'All' },
  { key: TIMELINE_TYPE_FILTER.OPD, label: 'OPD' },
  { key: TIMELINE_TYPE_FILTER.IPD, label: 'IPD' },
];

/**
 * TimelineFilterChips (F6) — All / OPD / IPD type filter, MANDATORY (not
 * polish, per the designer's named weakness of Variant A: a rare IPD
 * admission buried among forty OPD rows).
 *
 * `counts` MUST be computed by the caller from the SAME filtered/bucketed
 * collection that renders the rows (`PatientTimeline`'s single `rows`
 * selector) — this component never re-derives or re-counts anything itself,
 * so a header count can never drift from what's actually on screen.
 *
 * `showIpd=false` removes the IPD chip entirely (not "IPD · 0") — reserved
 * for `sections.admissions.status === MODULE_DISABLED`, decided by the
 * caller from the section status, never a client-side flag lookup here.
 *
 * A count the caller could not determine (its backing lane failed to read)
 * arrives as `COUNT_UNKNOWN` and renders as `—`. A missing count falls back
 * to the SAME `—`, never `0` — a zero here would re-assert the very absence
 * the lane-failure notice denies (Q2 §24).
 */
export default function TimelineFilterChips({ activeFilter, onChange, counts, showIpd = true }) {
  const chips = CHIP_DEFS.filter((c) => showIpd || c.key !== TIMELINE_TYPE_FILTER.IPD);
  return (
    <div
      role="group"
      aria-label="Timeline type filter"
      className="inline-flex flex-wrap gap-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-1"
    >
      {chips.map((c) => {
        const active = activeFilter === c.key;
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`timeline-chip-${c.key}`}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            className={
              active
                ? 'rounded-full bg-teal-600 px-2.5 py-1 text-xs font-bold text-white'
                : 'rounded-full bg-white dark:bg-slate-700 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600'
            }
          >
            {c.label} · {counts?.[c.key] ?? COUNT_UNKNOWN}
          </button>
        );
      })}
    </div>
  );
}
